import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Image as ImageIcon, Play, Trash2, Plus, Upload, Link, Loader2, Edit, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const GalleryManagement = () => {
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('photos');
  const token = localStorage.getItem('token');

  // Add photo form
  const [showAddPhoto, setShowAddPhoto] = useState(false);
  const [photoForm, setPhotoForm] = useState({ title: '', description: '', file: null, url: '' });
  const [uploadMode, setUploadMode] = useState('file');
  const [addingPhoto, setAddingPhoto] = useState(false);

  // Add video form
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [videoForm, setVideoForm] = useState({ title: '', description: '', videoId: '', videoSource: 'youtube', driveLink: '' });
  const [addingVideo, setAddingVideo] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/gallery`);
      setPhotos(res.data.photos || []);
      setVideos(res.data.videos || []);
    } catch (err) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const handleAddPhoto = async () => {
    if (!photoForm.title.trim()) { toast.error('Title is required'); return; }
    if (uploadMode === 'file' && !photoForm.file) { toast.error('Please select a photo'); return; }
    if (uploadMode === 'url' && !photoForm.url.trim()) { toast.error('Please enter photo URL'); return; }

    setAddingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('title', photoForm.title.trim());
      fd.append('description', photoForm.description.trim());
      if (uploadMode === 'file') {
        fd.append('file', photoForm.file);
      } else {
        fd.append('photoUrl', photoForm.url.trim());
      }
      await axios.post(`${API}/api/gallery/photo`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo added');
      setPhotoForm({ title: '', description: '', file: null, url: '' });
      setShowAddPhoto(false);
      fetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add photo');
    } finally {
      setAddingPhoto(false);
    }
  };

  const extractDriveFileId = (link) => {
    const match = link.match(/\/d\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : '';
  };

  const handleAddVideo = async () => {
    if (!videoForm.title.trim()) { toast.error('Title is required'); return; }

    let videoId = '';
    let videoSource = videoForm.videoSource;

    if (videoSource === 'youtube') {
      videoId = videoForm.videoId.trim();
      if (!videoId) { toast.error('YouTube Video ID is required'); return; }
    } else {
      const fileId = extractDriveFileId(videoForm.driveLink);
      if (!fileId) { toast.error('Invalid Google Drive link. Use a link like https://drive.google.com/file/d/FILE_ID/view'); return; }
      videoId = fileId;
    }

    setAddingVideo(true);
    try {
      await axios.post(`${API}/api/gallery/video`, {
        title: videoForm.title.trim(),
        description: videoForm.description.trim(),
        videoId,
        videoSource,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Video added');
      setVideoForm({ title: '', description: '', videoId: '', videoSource: 'youtube', driveLink: '' });
      setShowAddVideo(false);
      fetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add video');
    } finally {
      setAddingVideo(false);
    }
  };

  const handleDelete = async (itemId, type) => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    try {
      await axios.delete(`${API}/api/gallery/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${type} deleted`);
      fetchGallery();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = async (itemId) => {
    try {
      await axios.put(`${API}/api/gallery/${itemId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Updated');
      setEditingId(null);
      fetchGallery();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API}${url}`;
  };

  const getVideoEmbedUrl = (video) => {
    if (video.videoSource === 'gdrive') {
      return `https://drive.google.com/file/d/${video.videoId}/preview`;
    }
    return `https://www.youtube.com/embed/${video.videoId}`;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="gallery-management-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ImageIcon className="w-6 h-6 text-orange-600" /> Gallery Management
        </h1>
        <div className="flex gap-2">
          {activeTab === 'photos' && (
            <Button onClick={() => setShowAddPhoto(!showAddPhoto)} className="bg-orange-600 hover:bg-orange-700" data-testid="gallery-add-photo-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Photo
            </Button>
          )}
          {activeTab === 'videos' && (
            <Button onClick={() => setShowAddVideo(!showAddVideo)} className="bg-orange-600 hover:bg-orange-700" data-testid="gallery-add-video-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Video
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button variant={activeTab === 'photos' ? 'default' : 'outline'} onClick={() => setActiveTab('photos')} data-testid="gallery-tab-photos">
          <ImageIcon className="w-4 h-4 mr-2" /> Photos ({photos.length})
        </Button>
        <Button variant={activeTab === 'videos' ? 'default' : 'outline'} onClick={() => setActiveTab('videos')} data-testid="gallery-tab-videos">
          <Play className="w-4 h-4 mr-2" /> Videos ({videos.length})
        </Button>
      </div>

      {/* Add Photo Form */}
      {showAddPhoto && activeTab === 'photos' && (
        <Card className="p-5 mb-6 border-2 border-dashed border-orange-300 bg-orange-50/30" data-testid="gallery-add-photo-form">
          <h3 className="font-semibold text-gray-800 mb-4">Add New Photo</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Title *</Label>
              <Input value={photoForm.title} onChange={(e) => setPhotoForm(p => ({ ...p, title: e.target.value }))} placeholder="Photo title" data-testid="gallery-photo-title" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={photoForm.description} onChange={(e) => setPhotoForm(p => ({ ...p, description: e.target.value }))} placeholder="Photo description" data-testid="gallery-photo-desc" />
            </div>
          </div>
          <div className="mb-4">
            {uploadMode === 'file' ? (
              <div>
                <Label>Photo *</Label>
                <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setPhotoForm(p => ({ ...p, file: e.target.files?.[0] || null }))} className="mt-1 text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700" data-testid="gallery-photo-file" />
                <button onClick={() => setUploadMode('url')} className="text-xs text-orange-600 hover:underline mt-2 block">
                  Or paste image URL instead
                </button>
              </div>
            ) : (
              <div>
                <Label>Photo URL *</Label>
                <Input value={photoForm.url} onChange={(e) => setPhotoForm(p => ({ ...p, url: e.target.value }))} placeholder="https://example.com/photo.jpg" data-testid="gallery-photo-url" className="mt-1" />
                <button onClick={() => setUploadMode('file')} className="text-xs text-orange-600 hover:underline mt-2 block">
                  Or upload a file instead
                </button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAddPhoto} disabled={addingPhoto} className="bg-orange-600 hover:bg-orange-700" data-testid="gallery-photo-submit">
              {addingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Add Photo
            </Button>
            <Button variant="outline" onClick={() => setShowAddPhoto(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Add Video Form */}
      {showAddVideo && activeTab === 'videos' && (
        <Card className="p-5 mb-6 border-2 border-dashed border-orange-300 bg-orange-50/30" data-testid="gallery-add-video-form">
          <h3 className="font-semibold text-gray-800 mb-4">Add Video</h3>
          <div className="flex gap-3 mb-4">
            <button onClick={() => setVideoForm(p => ({ ...p, videoSource: 'youtube' }))} className={`text-sm px-4 py-1.5 rounded-full font-medium ${videoForm.videoSource === 'youtube' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
              YouTube
            </button>
            <button onClick={() => setVideoForm(p => ({ ...p, videoSource: 'gdrive' }))} className={`text-sm px-4 py-1.5 rounded-full font-medium ${videoForm.videoSource === 'gdrive' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
              Google Drive
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label>Title *</Label>
              <Input value={videoForm.title} onChange={(e) => setVideoForm(p => ({ ...p, title: e.target.value }))} placeholder="Video title" data-testid="gallery-video-title" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={videoForm.description} onChange={(e) => setVideoForm(p => ({ ...p, description: e.target.value }))} placeholder="Video description" data-testid="gallery-video-desc" />
            </div>
            <div>
              {videoForm.videoSource === 'youtube' ? (
                <>
                  <Label>YouTube Video ID *</Label>
                  <Input value={videoForm.videoId} onChange={(e) => setVideoForm(p => ({ ...p, videoId: e.target.value }))} placeholder="e.g. PqH3VYFK7gM" data-testid="gallery-video-id" />
                  <p className="text-xs text-gray-500 mt-1">The ID from youtube.com/watch?v=<strong>ID_HERE</strong></p>
                </>
              ) : (
                <>
                  <Label>Google Drive Link *</Label>
                  <Input value={videoForm.driveLink} onChange={(e) => setVideoForm(p => ({ ...p, driveLink: e.target.value }))} placeholder="https://drive.google.com/file/d/.../view" data-testid="gallery-video-drive-link" />
                  <p className="text-xs text-gray-500 mt-1">Paste the full Google Drive share link</p>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAddVideo} disabled={addingVideo} className="bg-orange-600 hover:bg-orange-700" data-testid="gallery-video-submit">
              {addingVideo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Add Video
            </Button>
            <Button variant="outline" onClick={() => setShowAddVideo(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Photos Grid */}
      {activeTab === 'photos' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="gallery-photos-grid">
          {photos.map(photo => (
            <div key={photo.id} className="bg-white rounded-xl overflow-hidden shadow-sm border group relative" data-testid={`gallery-photo-${photo.id}`}>
              <div className="aspect-square overflow-hidden bg-gray-100">
                <img src={resolveUrl(photo.url)} alt={photo.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              {editingId === photo.id ? (
                <div className="p-3 space-y-2">
                  <Input value={editForm.title} onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))} className="h-7 text-xs" />
                  <Input value={editForm.description} onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))} className="h-7 text-xs" />
                  <div className="flex gap-1">
                    <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => handleEdit(photo.id)}>
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingId(null)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-3">
                  <p className="text-sm font-medium text-gray-800 truncate">{photo.title}</p>
                  {photo.titleTranslated && (
                    <p className="text-xs font-medium text-gray-500 truncate">{photo.titleTranslated}</p>
                  )}
                  <p className="text-xs text-gray-500 truncate">{photo.description}</p>
                  <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditingId(photo.id); setEditForm({ title: photo.title, description: photo.description }); }}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(photo.id, 'photo')} data-testid={`gallery-delete-photo-${photo.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {photos.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-400">
              <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No photos in gallery. Click "Add Photo" to get started.</p>
            </div>
          )}
        </div>
      )}

      {/* Videos Grid */}
      {activeTab === 'videos' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-videos-grid">
          {videos.map(video => (
            <div key={video.id} className="bg-white rounded-xl overflow-hidden shadow-sm border group" data-testid={`gallery-video-${video.id}`}>
              <div className="relative pb-[56.25%] h-0 bg-gray-100">
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={getVideoEmbedUrl(video)}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              {editingId === video.id ? (
                <div className="p-4 space-y-2">
                  <Input value={editForm.title} onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))} className="h-8 text-sm" placeholder="Title" />
                  <Input value={editForm.description} onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))} className="h-8 text-sm" placeholder="Description" />
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleEdit(video.id)}>
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="p-4">
                  <p className="font-medium text-gray-800">{video.title}</p>
                  {video.titleTranslated && (
                    <p className="text-sm font-medium text-gray-500">{video.titleTranslated}</p>
                  )}
                  <p className="text-sm text-gray-500">{video.description}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {video.videoSource === 'gdrive' ? 'Google Drive' : 'YouTube'} | ID: {video.videoId}
                  </p>
                  <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="sm" variant="outline" onClick={() => { setEditingId(video.id); setEditForm({ title: video.title, description: video.description }); }}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(video.id, 'video')} data-testid={`gallery-delete-video-${video.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {videos.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-400">
              <Play className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No videos in gallery. Click "Add Video" to get started.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GalleryManagement;
